<script setup lang="ts">
import type { IconName } from '@ui/components/icon/types'
import type { ButtonSizes } from '@ui/styles/Styles'
import type { NuxtLinkProps } from 'nuxt/app'
import Icon from '@ui/components/icon/Icon.vue'
import { ButtonVariant, Sizes } from '@ui/styles/Styles'
import { blurFocus } from '@ui/utils/blurFocus'
import { resolveComponent } from 'vue'
import Loader from '../loader/ButtonLoader.vue'

interface Props {
  label?: string
  variant?: ButtonVariant
  size?: ButtonSizes
  disabled?: boolean
  loading?: boolean
  iconLeft?: IconName
  iconRight?: IconName
  to?: NuxtLinkProps['to']
}

withDefaults(defineProps<Props>(), {
  label: '',
  variant: ButtonVariant.Primary,
  size: Sizes.Luke,
  disabled: false,
  loading: false,
  iconLeft: '',
  iconRight: '',
})

const NuxtLink = resolveComponent('NuxtLink')
</script>

<template lang="pug">
component.ui-button(
  :is="to ? NuxtLink : 'button'"
  type="button"
  :to="to"
  :class="[size, variant, loading && 'loading', disabled && 'disabled']"
  :disabled="disabled || loading"
  @mousedown="blurFocus"
)
  Icon.icon(v-if="iconLeft" :name="iconLeft")
  slot
    span.button-text.text-ellipsis(v-if="label") {{ $t(label) }}
  Icon.icon(v-if="iconRight" :name="iconRight")
  Loader.loader.row-4(v-if="loading")
</template>

<style scoped>
.ui-button {
  --button-bg: transparent;
  --button-border: transparent;
  --button-content: var(--cue-content-neutral-primary);
  --button-icon: var(--button-content);
  --button-loader: var(--button-content);
  --button-focus-inner: var(--cue-bg-surface-primary);
  --button-focus-outer: var(--cue-border-primary-soft);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  box-sizing: border-box;
  cursor: pointer;
  border: 1px solid var(--button-border);
  position: relative;
  background: var(--button-bg);
  color: var(--button-content);
  text-decoration: none;
  transition: 0.3s ease;
  transition-property: color, background-color, border-color, box-shadow, opacity;

  .icon {
    display: inline-flex;
    flex-shrink: 0;
    color: var(--button-icon, var(--button-content));
  }

  .button-text {
    color: var(--button-content);
  }

  /* Button Sizes from Figma - using UnoCSS classes */
  &.r2d2 {
    --uno: h-28 p-4-16 typ-14-20-400 br-10 row-8;

    .icon {
      --uno: s-16;
    }
  }

  &.leia {
    --uno: h-36 p-8-16 typ-14-20-500 br-12 row-12;

    .icon {
      --uno: s-20;
    }
  }

  &.hanSolo {
    --uno: h-40 p-10-16 typ-14-20-500 br-14 row-12;

    .icon {
      --uno: s-20;
    }
  }

  &.luke {
    --uno: h-48 p-12-24 typ-16-24-500 br-16 row-12;

    .icon {
      --uno: s-24;
    }
  }

  &.accent {
    --button-bg: var(--cue-bg-accent-default);
    --button-content: var(--cue-bg-accent-on-default);

    &:not(:disabled) {
      &:hover {
        --button-bg: var(--cue-bg-accent-default-hover);
      }

      &:active {
        --button-bg: var(--cue-bg-accent-default-pressed);
      }
    }

    &.disabled {
      --button-bg: var(--cue-bg-neutral-disabled);
      --button-content: var(--cue-content-neutral-disabled);
      cursor: not-allowed;
    }
  }

  &.primary {
    --button-bg: var(--cue-bg-primary-default);
    --button-content: var(--cue-bg-primary-on-default);

    &:not(:disabled) {
      &:hover {
        --button-bg: var(--cue-bg-primary-default-hover);
      }

      &:active {
        --button-bg: var(--cue-bg-primary-default-pressed);
      }
    }

    &.disabled {
      --button-bg: var(--cue-bg-neutral-disabled);
      --button-content: var(--cue-content-neutral-disabled);
      cursor: not-allowed;
    }
  }

  &.secondary {
    --button-bg: var(--cue-bg-primary-soft-dark);
    --button-content: var(--cue-content-primary-on-soft);

    &:not(:disabled) {
      &:hover {
        --button-bg: var(--cue-bg-primary-soft-dark-hover);
      }

      &:active {
        --button-bg: var(--cue-bg-primary-soft-dark-pressed);
      }
    }

    &.disabled {
      --button-bg: var(--cue-bg-neutral-disabled);
      --button-content: var(--cue-content-neutral-disabled);
      cursor: not-allowed;
    }
  }

  &.outline {
    --button-bg: var(--cue-bg-neutral-none);
    --button-border: var(--cue-border-primary-default);
    --button-content: var(--cue-content-primary-default);

    &:not(:disabled) {
      &:hover {
        --button-bg: var(--cue-bg-primary-soft-hover);
        --button-content: var(--cue-content-primary-on-soft);
      }

      &:active {
        --button-bg: var(--cue-bg-primary-soft-pressed);
        --button-content: var(--cue-content-primary-on-soft);
      }
    }

    &.disabled {
      --button-bg: var(--cue-bg-neutral-disabled);
      --button-border: var(--cue-border-neutral-disabled);
      --button-content: var(--cue-content-neutral-disabled);
      cursor: not-allowed;
    }
  }

  &.ghost {
    --button-bg: var(--cue-bg-neutral-none);
    --button-content: var(--cue-content-primary-default);

    &:not(:disabled) {
      &:hover {
        --button-bg: var(--cue-bg-primary-soft-dark-hover);
        --button-content: var(--cue-content-primary-on-soft);
      }

      &:active {
        --button-bg: var(--cue-bg-primary-soft-dark-pressed);
        --button-content: var(--cue-content-primary-on-soft);
      }
    }

    &.disabled {
      --button-bg: var(--cue-bg-neutral-disabled);
      --button-content: var(--cue-content-neutral-disabled);
      cursor: not-allowed;
    }
  }

  &.static {
    --button-bg: var(--cue-static-neutral-main);
    --button-content: var(--cue-static-primary-default);
    --button-focus-inner: var(--cue-static-neutral-main);
    --button-focus-outer: var(--cue-static-primary-soft-dark);

    &:not(:disabled) {
      &:hover {
        opacity: 0.85;
      }

      &:active {
        opacity: 0.7;
      }
    }

    &.disabled {
      --button-bg: var(--cue-bg-neutral-disabled);
      --button-content: var(--cue-content-neutral-disabled);
      opacity: 1;
      cursor: not-allowed;
    }
  }

  &.function_accent {
    --button-content: var(--cue-content-primary-default);

    &:not(:disabled) {
      &:hover {
        --button-content: var(--cue-content-primary-hover);
      }

      &:active {
        --button-content: var(--cue-content-primary-pressed);
      }
    }

    &.disabled {
      --button-content: var(--cue-content-neutral-disabled);
      cursor: not-allowed;
    }
  }

  &.function {
    --button-content: var(--cue-content-neutral-primary);

    &:not(:disabled) {
      &:hover {
        --button-content: var(--cue-content-neutral-secondary);
      }

      &:active {
        --button-content: var(--cue-content-neutral-tertiary);
      }
    }

    &.disabled {
      --button-content: var(--cue-content-neutral-disabled);
      cursor: not-allowed;
    }
  }

  &.function_accent,
  &.function {
    border: none;
    --uno: p-0;

    &.r2d2 {
      --uno: h-20;
    }

    &.leia {
      --uno: h-20;
    }

    &.hanSolo {
      --uno: h-20;
    }

    &.luke {
      --uno: h-24;
    }
  }

  &:not(:disabled):focus-visible {
    box-shadow:
      0 0 0 2px var(--button-focus-inner),
      0 0 0 6px var(--button-focus-outer);
  }

  &.loading {
    color: transparent !important;
    pointer-events: none;

    > *:not(.loader) {
      opacity: 0 !important;
    }
  }

  .loader {
    --uno: position-absolute l-0 t-0 w-100p h-100p;
    color: var(--button-loader, var(--button-content));
  }

  &:disabled {
    cursor: not-allowed;
  }
}

@keyframes spin {
  to {
    transform: translate(-50%, -50%) rotate(360deg);
  }
}
</style>
